import CloseRounded from "@mui/icons-material/CloseRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import { kickNavy } from "~/shared/styles/baseStyles";
import {
  looksLikeMedicationList,
  reviewableMedicationRows,
} from "../utils/medicationList";
import MedicationListReview from "./medicationListReview";
import {
  medicationHeadingStyles,
  medicationInputStyles,
  medicationPrimaryButtonStyles,
  medicationSecondaryButtonStyles,
} from "./medicationReviewStyles";

// zIndex lifts the drawer above an open dialog (the EMR medication editor); a
// MUI drawer otherwise sits below dialogs.
//
// nameLocked shows the name read-only: a medication chosen from the catalog
// search is one known product, so the patient changes dose and frequency only
// and removes it to pick something else.
//
// previewList asks the medication extractor whether a typed name is one
// medication or several; with onSaveList, the rows it finds open the list
// review. Without them a typed name is saved as it is.
export default function MedicationDetails({
  entry,
  isNew,
  nameLocked = false,
  onSave,
  onRemove,
  onClose,
  zIndex,
  previewList,
  onSaveList,
}) {
  const [draft, setDraft] = useState({ ...entry });
  const [checking, setChecking] = useState(false);
  // rows: separate medications to review. null: the extractor could not
  // separate a name that reads like a list, so the patient decides.
  const [review, setReview] = useState(null);
  // The extractor read one medication in a name too long to save as it is.
  const [singleTooLong, setSingleTooLong] = useState(false);
  const pending = useRef(null);
  useEffect(() => () => pending.current?.abort(), []);

  const name = (draft.name || "").trim();
  const canReviewList = Boolean(previewList && onSaveList);
  const sameMedication = name === (entry.name || "").trim();
  // Only a name the patient typed can be a list: a manual entry ("I don’t see
  // my medication listed"), a pasted list, or a rename. A catalog name is one
  // product however many commas it contains, and a saved name the patient has
  // not touched was already accepted as it is.
  const typedName = !nameLocked && (isNew || !sameMedication);
  const checksForList = canReviewList && typedName;
  const nameLimit = checksForList && !singleTooLong ? 12000 : 300;
  const nameTooLong = name.length > nameLimit;
  const productDetails = [
    entry.productStrength && `Strength on the label: ${entry.productStrength}`,
    entry.form,
    entry.route,
  ].filter(Boolean);

  const saveSingle = () => {
    if (!name || name.length > 300) return;
    // A renamed medication no longer has the old product's identifiers or
    // formulation. Dose and frequency edits do not change product identity.
    onSave({
      ...draft,
      name,
      dose: (draft.dose || "").trim(),
      frequency: (draft.frequency || "").trim(),
      ...(!sameMedication
        ? { rxCUI: "", ndc: "", productStrength: "", form: "", route: "" }
        : {}),
    });
  };
  // Punctuation alone cannot tell "ambien. xanax" from one product, so every
  // typed name is checked by the extractor. Anything it cannot settle keeps
  // the name as typed, except a name that reads like a list: that is shown for
  // review so a list is never saved as one medication by accident.
  const checkForList = async () => {
    const controller = new AbortController();
    pending.current = controller;
    setChecking(true);
    let rows = null;
    try {
      rows = reviewableMedicationRows(
        await previewList(name, controller.signal),
      );
    } catch {
      rows = null;
    }
    // The drawer closed while waiting.
    if (controller.signal.aborted) return;
    pending.current = null;
    setChecking(false);
    if (rows?.length > 1) setReview({ rows });
    else if (!rows && looksLikeMedicationList(name)) setReview({ rows: null });
    else if (name.length > 300) setSingleTooLong(true);
    else saveSingle();
  };
  const save = (event) => {
    event.preventDefault();
    if (!name || nameTooLong || checking || review) return;
    if (checksForList) checkForList();
    else saveSingle();
  };

  return (
    <Drawer
      className="ph-no-capture"
      anchor="right"
      open
      onClose={onClose}
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "medication-details-heading",
        sx: {
          width: { xs: "100%", sm: 460 },
          maxWidth: "100%",
          color: kickNavy,
          fontFamily:
            '"BasisGrotesquePro-Regular", Helvetica, Arial, sans-serif',
          "& .MuiButton-root, & .MuiInputBase-root": { fontFamily: "inherit" },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={save}
        sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 3 }}>
          <Typography
            component="h2"
            id="medication-details-heading"
            sx={{ ...medicationHeadingStyles, flex: 1 }}
          >
            {review
              ? "Review separate medications"
              : isNew
                ? "Add a medication"
                : "Medication details"}
          </Typography>
          <IconButton
            aria-label="Close medication details"
            onClick={onClose}
            sx={{ color: kickNavy, width: 44, height: 44 }}
          >
            <CloseRounded />
          </IconButton>
        </Box>
        {review ? (
          <MedicationListReview
            entry={{ ...draft, name }}
            rows={review.rows}
            onSave={onSaveList}
            onBack={() => setReview(null)}
            onKeepSingle={saveSingle}
          />
        ) : (
          <>
            <Box sx={{ display: "grid", gap: 3, px: 3, pb: 3 }}>
              <Typography sx={{ color: "#626176", fontSize: "15px" }}>
                Add the details you know. It’s OK to leave the dose and
                frequency blank.
              </Typography>
              {[
                ["name", "Medication or supplement name", "e.g. Vitamin D"],
                ["dose", "Dose (optional)", "e.g. 25 mg or 1 tablet"],
                [
                  "frequency",
                  "How often (optional)",
                  "e.g. Once a day or as needed",
                ],
              ].map(([field, label, placeholder], index) =>
                field === "name" && nameLocked ? (
                  <Box key={field}>
                    <Typography sx={{ fontSize: "13px", color: "#626176" }}>
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {entry.name}
                    </Typography>
                    <Typography
                      sx={{ mt: 0.5, fontSize: "13px", color: "#626176" }}
                    >
                      To change it, remove this medication and search again.
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    key={field}
                    id={`medication-detail-${field}`}
                    label={label}
                    placeholder={placeholder}
                    value={draft[field] || ""}
                    onChange={(event) => {
                      if (field === "name") setSingleTooLong(false);
                      setDraft({ ...draft, [field]: event.target.value });
                    }}
                    // The check answers for the name as submitted.
                    disabled={checking}
                    autoFocus={index === (isNew ? 0 : 1)}
                    fullWidth
                    multiline={field === "name"}
                    maxRows={field === "name" ? 6 : undefined}
                    required={field === "name"}
                    // Keep pasted lists intact so they can be reviewed; show a
                    // length error instead of silently truncating the source.
                    inputProps={{
                      maxLength:
                        field === "name" && canReviewList ? undefined : 300,
                    }}
                    error={field === "name" && nameTooLong}
                    helperText={
                      field === "name" && nameTooLong
                        ? `Please shorten this ${nameLimit === 300 ? "name" : "list"} to ${nameLimit.toLocaleString("en-US")} characters or fewer.`
                        : undefined
                    }
                    sx={medicationInputStyles}
                  />
                ),
              )}
              {sameMedication && productDetails.length > 0 && (
                <Box
                  sx={{ p: 2, backgroundColor: "#faf9fc", borderRadius: "6px" }}
                >
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}
                  >
                    Previously reported product details
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "#626176" }}>
                    {productDetails.join(" · ")}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                mt: "auto",
                p: 3,
                pb: "max(24px, env(safe-area-inset-bottom))",
                borderTop: "1px solid #e9e6ef",
                backgroundColor: "#fff",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={!name || nameTooLong || checking}
                sx={medicationPrimaryButtonStyles}
              >
                {checking
                  ? "Checking…"
                  : isNew
                    ? "Add medication"
                    : "Save changes"}
              </Button>
              {!isNew && onRemove && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={onRemove}
                  sx={medicationSecondaryButtonStyles}
                >
                  Remove this medication
                </Button>
              )}
              <Button
                type="button"
                onClick={onClose}
                sx={medicationSecondaryButtonStyles}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
